import { BaseSyntheticEvent, useEffect, useState } from "react";
import { View } from "react-native";
import { ActivityIndicator, Text } from "react-native-paper";
import { Control, useForm } from "react-hook-form";
import moment from "moment";
import { ActionsContainer, Button, ScreenContainer, ScrollableContainer } from "../../components";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  CREATE_EVENT_PAYMENT_INTENT_MUTATION,
  CREATE_EVENT_REGISTRATION,
  CURRENT_PERSON_QUERY,
  EVENT_DETAILS_FRAGMENT,
  EVENT_ELIGIBILITY_QUERY,
  EVENT_REGISTRATION_QUERY,
  OFFLINE_FEE_FRAGMENT,
  ORDER_PREVIEW_FRAGMENT,
} from "./queries";
import {
  CreateEventPaymentIntentInput,
  CreateEventRegistrationMutation,
  CreatePaymentIntentResponseType,
  CurrentPersonQuery,
  EventDetailsQuery,
  EventEligibilityQuery,
  EventRegistrationQuery,
  FormData,
  PaymentIntentResponseType,
  PaymentMethod,
  RegistrationStatus,
  RegistrationType,
} from "./types";
import { ScreenProps } from "../types";
import { readableDate } from "../../utils/dateUtils";
import { camelCase, cond, stubTrue } from "lodash";
import {
  afterRegistrationClose,
  beforeRegistrationOpen,
  getFormDefaultValues,
  selectedSessions,
  setDefaultSessionValues,
  hasRequiredFields,
  formatEventDate,
  isPAT,
} from "./helpers";
import { useFeatureFlags } from "../../hooks/useFlagr";
import StripeCheckout from "../../components/StripeCheckout/StripeCheckout";
import { CURRENT_MEMBER_FRAGMENT } from "../fragments";
import SessionOptions from "./SessionOptions";
import { DateOfBirthField, DisclaimerField, GenderField, AdditionalGuestsComponent } from "./CustomFields";
import PaymentSection from "./PaymentSection";
import RegistrationFullModal from "./modals/RegistrationFullModal";
import EventSchedule from "./EventSchedule";
import { AgeRestrictionModal, DateOfBirthModal } from "./modals";
import { encode } from "base-64";

export const EVENT_DETAILS_QUERY = gql`
  query EventDetailsQuery($slug: String!) {
    event(slug: $slug) {
      ...EventDetailsFragment
      ...OrderPreviewFragment
      ...OfflineFeeFragment
    }
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${EVENT_DETAILS_FRAGMENT}
  ${ORDER_PREVIEW_FRAGMENT}
  ${OFFLINE_FEE_FRAGMENT}
  ${CURRENT_MEMBER_FRAGMENT}
`;

export const ButtonContainer = ({
  event,
  registrationEligible,
  onSubmit,
  isSubmitting,
  isValid,
  invalidError,
  setInvalidError,
  isRegistered,
  handlePaymentModalVisible,
  requiresPayment,
  hasValidSpecialReg,
}: {
  event: EventDetailsQuery["event"];
  registrationEligible: boolean;
  onSubmit(e?: BaseSyntheticEvent): void;
  isSubmitting: boolean;
  isValid: boolean;
  invalidError: string;
  setInvalidError: (error: string) => void;
  isRegistered: boolean;
  handlePaymentModalVisible: () => void;
  requiresPayment: boolean;
  hasValidSpecialReg?: boolean;
}) => {
  const registerButton = cond([
    [
      () => isRegistered,
      () => (
        <View>
          <Button mode="contained" disabled testID="register-button">
            Already Registered
          </Button>
        </View>
      ),
    ],
    [
      beforeRegistrationOpen,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Opens {readableDate(new Date(event.registrationOpenAt))}
        </Button>
      ),
    ],
    [
      (event) => afterRegistrationClose(event) && !hasValidSpecialReg,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Closed
        </Button>
      ),
    ],
    [
      (event) => event.registrationFull && !hasValidSpecialReg,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Full
        </Button>
      ),
    ],
    [
      () => !registrationEligible,
      () => (
        <View>
          <Button mode="contained" disabled testID="register-button">
            Registration Unavailable
          </Button>
        </View>
      ),
    ],
    [
      () => requiresPayment,
      () => (
        <View>
          <Button
            mode="contained"
            loading={isSubmitting}
            disabled={isSubmitting}
            isValid={isValid}
            onPress={handlePaymentModalVisible}
            testID={isValid ? "payment-button" : "disabled-payment-button"}
          >
            Continue to Payment
          </Button>
        </View>
      ),
    ],
    [
      stubTrue,
      () => (
        <Button
          mode="contained"
          isValid={isValid}
          loading={isSubmitting}
          disabled={isSubmitting}
          onPress={onSubmit}
          testID="register-button"
        >
          Register
        </Button>
      ),
    ],
  ]);
  return (
    <ActionsContainer
      errorAlert={!!invalidError}
      errorTitle="Missing Required Information"
      errorMessage={invalidError}
      onCloseErrorMessage={() => setInvalidError("")}
    >
      {registerButton(event)}
    </ActionsContainer>
  );
};

const RegistrantInfoFields = ({
  control,
  currentPerson,
  customFields,
}: {
  control: Control<FormData>;
  currentPerson: CurrentPersonQuery["currentPerson"];
  customFields: string[];
}) => {
  const hasExistingDateOfBirth = !!currentPerson?.dateOfBirth;
  const [dateOfBirthModalVisible, setDateOfBirthModalVisible] = useState(false);
  return (
    <View>
      <View className="bg-sky-light h-[1px] my-8" />
      <View className="mb-6">
        <Text className="text-large-normal font-700">Registrant Information</Text>
        <Text className="text-regular-normal mt-2">
          Age and gender are collected for PAT operational purposes, including proper yardage determinations and calculation of
          the PAT target score.
        </Text>
      </View>
      <View>
        {customFields.includes("dateOfBirth") && (
          <DateOfBirthField
            control={control}
            hasExistingDateOfBirth={hasExistingDateOfBirth}
            setDateOfBirthModalVisible={setDateOfBirthModalVisible}
          />
        )}
        {hasExistingDateOfBirth && (
          <DateOfBirthModal visible={dateOfBirthModalVisible} onDismiss={() => setDateOfBirthModalVisible(false)} />
        )}
        {customFields.includes("gender") && <GenderField control={control} />}
      </View>
    </View>
  );
};

export default function ({ navigation, route }: ScreenProps<"EventRegistration">) {
  const [invalidError, setInvalidError] = useState<string>("");
  const [visible, setVisible] = useState<boolean>(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState<boolean>(false);
  const [ageRestrictionVisible, setAgeRestrictionVisible] = useState<boolean>(false);
  const [paymentIntentResponse, setPaymentIntentResponse] = useState<PaymentIntentResponseType>();
  const [createPaymentIntent] = useMutation<CreatePaymentIntentResponseType>(CREATE_EVENT_PAYMENT_INTENT_MUTATION);

  const flags = useFeatureFlags();

  const { loading, data } = useQuery<EventDetailsQuery>(EVENT_DETAILS_QUERY, {
    variables: { slug: route.params.slug },
  });
  const { event } = data || {};
  const orderPreview = event?.orderPreview ?? null;
  const offlineFee = event?.offlineFee ?? null;
  const customFields = event?.customFields.map((x) => camelCase(x)) ?? [];
  const requiresPayment = !!orderPreview || !!offlineFee;

  const { data: registrationData, loading: registrationLoading } = useQuery<EventRegistrationQuery>(EVENT_REGISTRATION_QUERY, {
    variables: { slug: route.params.slug, status: RegistrationStatus.REGISTERED },
  });

  const { data: personData } = useQuery<CurrentPersonQuery>(CURRENT_PERSON_QUERY);

  const defaultValues = getFormDefaultValues(personData?.currentPerson, event);

  const {
    watch,
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    if (data && data.event) {
      const sessionDefaults = setDefaultSessionValues(data.event);
      setValue("sessions", sessionDefaults);

      if (customFields.length > 0) {
        const formattedDateOfBirth = personData?.currentPerson?.dateOfBirth
          ? moment(personData.currentPerson.dateOfBirth).format("MM/DD/YYYY")
          : "";
        setValue("dateOfBirth", formattedDateOfBirth);
        setValue("gender", "");
      }
    }
  }, [data, setValue, personData]);

  useEffect(() => {
    if (event && requiresPayment) {
      if (paymentIntentResponse) {
        return;
      }

      const paymentIntentInput: CreateEventPaymentIntentInput = {
        eventId: event.id,
        paymentMethod: PaymentMethod.Card,
      };

      const getPaymentData = async (input: CreateEventPaymentIntentInput) => {
        try {
          const { data } = await createPaymentIntent({ variables: { input: { ...input } } });
          setPaymentIntentResponse(data?.createPaymentIntent);
        } catch (error: any) {
          return error.message || "Unexpected error. Please try again.";
        }
      };
      getPaymentData(paymentIntentInput);
    }
  }, [paymentIntentResponse, event, requiresPayment]);

  const [createEventRegistration] = useMutation<CreateEventRegistrationMutation>(CREATE_EVENT_REGISTRATION);
  const { data: eligibilityData } = useQuery<EventEligibilityQuery>(EVENT_ELIGIBILITY_QUERY, {
    variables: { slug: route.params.slug },
  });
  const registrationEligible = !!eligibilityData?.eventEligibility?.isEligible;

  if (loading || registrationLoading || !event) {
    return <ActivityIndicator className="flex-1 items-center justify-center" testID="query-loader" />;
  }

  const shouldShowForm = !requiresPayment || (flags["membership-pat-registration"] && requiresPayment);

  const isRegistered = !!registrationData?.eventRegistration?.registeredAt;
  const specialRegParam = (route.params as { special_reg?: string })?.special_reg;
  const hasValidSpecialReg = !!(event && specialRegParam && specialRegParam === encode(event.id));

  if (isRegistered) {
    navigation.navigate("EventConfirmation", { slug: route.params.slug });
  }

  const onReviewAvailability = () =>
    navigation.navigate("EventDetails", { slug: route.params.slug, special_reg: specialRegParam });
  const hasFormErrors = Object.keys(errors).length > 0;

  const isUnder18 = (dob?: string) => {
    if (!dob) return false;
    const dobMoment = moment(dob, "MM/DD/YYYY", true);
    if (!dobMoment.isValid()) return false;
    return moment(event.startsAt).diff(dobMoment, "years") < 18;
  };

  async function handlePaymentModalVisible() {
    await trigger();
    if (!hasFormErrors && isValid) {
      if (!event) {
        return;
      }
      if (customFields.includes("dateOfBirth") && isUnder18(watch("dateOfBirth"))) {
        setAgeRestrictionVisible(true);
        return;
      }
      setPaymentModalVisible(true);
    }
  }

  const registrantInfoFields = ["dateOfBirth", "gender"];
  const optionalFields = ["additionalGuests", "showAdditionalGuests"];
  const requiredFields = [
    ...customFields.filter((x) => !optionalFields.includes(x)),
    ...(watch("showAdditionalGuests") ? ["additionalGuests"] : []),
  ];

  const isTermsContentAvailable = event?.termsContent;
  const isTermsAccepted = watch("disclaimer");
  const isSessionSelected = selectedSessions(watch("sessions")).length > 0;
  const hasValidPaymentIntentResponse = !requiresPayment || !!paymentIntentResponse;

  const validationChecks = [
    () => isSessionSelected,
    () => !isTermsContentAvailable || isTermsAccepted,
    () => hasRequiredFields(watch(), requiredFields),
    () => hasValidPaymentIntentResponse,
    () => !hasFormErrors,
  ];
  const isValid = validationChecks.every((check) => check());

  const onSubmit = (event: EventDetailsQuery["event"]) => async (data: FormData) => {
    if (customFields.includes("dateOfBirth") && isUnder18(data.dateOfBirth)) {
      setAgeRestrictionVisible(true);
      return;
    }
    const dateOfBirthMoment = data.dateOfBirth ? moment(data.dateOfBirth, "MM/DD/YYYY") : null;
    const shouldAcceptTerms = !!event.termsContent;
    const hasAcceptedTerms = data.disclaimer;
    const sessions = selectedSessions(data.sessions);
    const hasSelectedSessions = sessions.length > 0;

    if ((shouldAcceptTerms && !(hasAcceptedTerms && hasSelectedSessions)) || !hasSelectedSessions) {
      const errorMessage = shouldAcceptTerms
        ? "Please accept the terms and select at least one session to attend."
        : "Please select at least one session to attend.";
      setInvalidError(errorMessage);
      return;
    }

    const customFieldsData = {
      ...(customFields.includes("disclaimer") && { disclaimer: data.disclaimer }),
      ...(customFields.includes("gender") && { gender: data.gender }),
      ...(customFields.includes("dateOfBirth") &&
        dateOfBirthMoment && {
          dateOfBirth: dateOfBirthMoment.format("YYYY-MM-DD"),
        }),
      ...(customFields.includes("additionalGuests") && { additionalGuests: data.additionalGuests }),
    };

    const input = {
      eventId: event.id,
      sessions,
      acceptedTerms: hasAcceptedTerms,
      ...customFieldsData,
      ...(isPAT(event) && { patRegistration: { orderId: paymentIntentResponse?.orderId } }),
      ...(specialRegParam && { specialReg: specialRegParam }),
    };

    const { data: createEventRegistrationResponse } = await createEventRegistration({
      variables: {
        input,
      },
    });
    const { message, success } = createEventRegistrationResponse?.createEventRegistration || {};

    if (success) {
      navigation.navigate("EventConfirmation", { slug: route.params.slug });
    } else {
      message == "EventSession registration is full"
        ? setVisible(true)
        : navigation.navigate("EventDetails", { slug: route.params.slug, special_reg: specialRegParam });
    }
  };

  return shouldShowForm ? (
    <ScreenContainer navigationTitle="Event Registration">
      <ScrollableContainer>
        <View className="m-6">
          <Text className="text-title-5 font-700">{event?.title}</Text>
          <Text className="text-regular-normal mt-1">{event?.location}</Text>
          <Text className="text-regular-normal mt-1">{formatEventDate(event?.startsAt, event?.endsAt, event?.timezone)}</Text>
          <Text
            className="text-regular-normal underline text-primary mt-1"
            onPress={() =>
              navigation.navigate("EventDetails", {
                slug: route.params.slug,
                special_reg: specialRegParam,
              })
            }
          >
            View Event Details
          </Text>
          <View>
            {customFields.filter((x) => registrantInfoFields.includes(x)).length > 0 && personData?.currentPerson && (
              <RegistrantInfoFields control={control} currentPerson={personData.currentPerson} customFields={customFields} />
            )}
            {event.registrationType === RegistrationType.ALL && <EventSchedule event={event} control={control} />}
            {event.registrationType === RegistrationType.INDIVIDUAL && <SessionOptions event={event} control={control} />}
            {requiresPayment && <PaymentSection offlineFee={offlineFee} orderPreview={orderPreview} />}
          </View>
          {customFields.includes("additionalGuests") && <AdditionalGuestsComponent control={control} event={event} />}
          {(customFields.includes("disclaimer") || !!event.termsContent) && <DisclaimerField control={control} event={event} />}
        </View>
        {event && (
          <ButtonContainer
            onSubmit={handleSubmit(onSubmit(event))}
            {...{
              event,
              registrationEligible,
              isSubmitting,
              isValid,
              invalidError,
              setInvalidError,
              isRegistered,
              handlePaymentModalVisible,
              requiresPayment,
              hasValidSpecialReg,
            }}
          />
        )}
        <RegistrationFullModal {...{ onReviewAvailability, visible, setVisible }} />
        {paymentIntentResponse && isValid && (
          <StripeCheckout
            visible={paymentModalVisible}
            setIsVisible={setPaymentModalVisible}
            paymentIntentResponse={paymentIntentResponse}
            onSubmit={handleSubmit(onSubmit(event))}
          />
        )}
        <AgeRestrictionModal visible={ageRestrictionVisible} setVisible={setAgeRestrictionVisible} />
      </ScrollableContainer>
    </ScreenContainer>
  ) : null;
}
